import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Download, Play, RefreshCw, Wand2 } from "lucide-react";
import { api, type ImportJob } from "@/lib/api";
import { ErrorState } from "@/components/empty-state";

export const Route = createFileRoute("/admin/import")({
  component: AdminImport,
});

function AdminImport() {
  const qc = useQueryClient();
  const jobs = useQuery({ queryKey: ["admin-jobs"], queryFn: () => api.adminImportJobs(), refetchInterval: 3000 });
  const errors = useQuery({ queryKey: ["admin-import-errors"], queryFn: () => api.adminImportErrors() });
  const incompleteCount = useQuery({
    queryKey: ["admin-incomplete-count"],
    queryFn: () => api.adminImportIncompleteCount(),
    refetchInterval: 10000,
  });
  const [appid, setAppid] = useState("");
  const [bulkLimit, setBulkLimit] = useState(50);
  const [incompleteLimit, setIncompleteLimit] = useState(200);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin-jobs"] });
    qc.invalidateQueries({ queryKey: ["admin-incomplete-count"] });
  };

  const list = useMutation({ mutationFn: () => api.adminImportAppList(), onSuccess: invalidate });
  const bulk = useMutation({ mutationFn: () => api.adminImportBulk(bulkLimit), onSuccess: invalidate });
  const single = useMutation({ mutationFn: () => api.adminImportApp(Number(appid)), onSuccess: invalidate });
  const incomplete = useMutation({ mutationFn: () => api.adminImportIncomplete(incompleteLimit), onSuccess: invalidate });

  const runningJob = jobs.data?.data.find((j) => j.status === "running") ?? null;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-2xl font-bold">Steam import</h1>
        <p className="text-sm text-muted-foreground">
          Uses official Steam endpoints. Rate limiting and retries are configured server-side via env vars.
        </p>
      </header>

      {runningJob && <RunningJobBanner job={runningJob} />}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Card title="1. Fetch app list" description="Pulls every Steam app ID + name and stores them as pending. ~200k rows, slow.">
          <button onClick={() => list.mutate()} disabled={list.isPending} className="inline-flex items-center gap-1 rounded-md bg-primary-gradient px-3 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">
            <Download className="h-4 w-4" /> {list.isPending ? "Running…" : "Run"}
          </button>
        </Card>
        <Card title="2. Bulk import details" description="Pulls full storefront data for the next N pending apps.">
          <div className="flex items-center gap-2">
            <input type="number" min={1} max={25000} value={bulkLimit} onChange={(e) => setBulkLimit(Number(e.target.value))} className="w-24 rounded-md border border-border bg-surface px-2 py-1.5 text-sm" />
            <button onClick={() => bulk.mutate()} disabled={bulk.isPending} className="inline-flex items-center gap-1 rounded-md bg-primary-gradient px-3 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">
              <Play className="h-4 w-4" /> {bulk.isPending ? "Running…" : "Run"}
            </button>
          </div>
        </Card>
        <Card
          title="3. Reimport incomplete games"
          description={
            incompleteCount.isSuccess
              ? `${incompleteCount.data.data.count.toLocaleString()} games are missing Steam data (header, description, platforms, requirements, editions…). Only these are refreshed.`
              : "Refresh games that are missing important Steam data."
          }
        >
          <div className="flex items-center gap-2">
            <input type="number" min={1} max={25000} value={incompleteLimit} onChange={(e) => setIncompleteLimit(Number(e.target.value))} className="w-24 rounded-md border border-border bg-surface px-2 py-1.5 text-sm" />
            <button onClick={() => incomplete.mutate()} disabled={incomplete.isPending} className="inline-flex items-center gap-1 rounded-md bg-primary-gradient px-3 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">
              <Wand2 className="h-4 w-4" /> {incomplete.isPending ? "Running…" : "Run"}
            </button>
          </div>
          {incomplete.isError && <p className="mt-2 text-xs text-destructive">{(incomplete.error as Error).message}</p>}
        </Card>
        <Card title="4. Import single appid" description="Re-import a single Steam app immediately.">
          <div className="flex items-center gap-2">
            <input value={appid} onChange={(e) => setAppid(e.target.value)} placeholder="e.g. 1245620" className="w-32 rounded-md border border-border bg-surface px-2 py-1.5 text-sm" />
            <button onClick={() => appid && single.mutate()} disabled={single.isPending || !appid} className="inline-flex items-center gap-1 rounded-md bg-primary-gradient px-3 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">
              <RefreshCw className="h-4 w-4" /> {single.isPending ? "Running…" : "Import"}
            </button>
          </div>
          {single.isError && <p className="mt-2 text-xs text-destructive">{(single.error as Error).message}</p>}
          {single.isSuccess && <p className="mt-2 text-xs text-success">Imported: {single.data.data.title}</p>}
        </Card>
      </div>

      <div>
        <h2 className="mb-3 font-display text-lg font-semibold">Recent import jobs</h2>
        {jobs.isError && <ErrorState message={(jobs.error as Error).message} />}
        {jobs.isSuccess && jobs.data.data.length === 0 && <p className="text-sm text-muted-foreground">No imports have run yet.</p>}
        {jobs.isSuccess && jobs.data.data.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-surface text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Started</th>
                  <th className="px-4 py-3 text-right">Imported / Failed / Total</th>
                </tr>
              </thead>
              <tbody>
                {jobs.data.data.map((j) => (
                  <tr key={j.id} className="border-t border-border/60">
                    <td className="px-4 py-2">{j.source}</td>
                    <td className="px-4 py-2">
                      <span className={
                        j.status === "completed" ? "text-success" :
                        j.status === "running" ? "text-primary" :
                        j.status === "failed" ? "text-destructive" : "text-muted-foreground"
                      }>{j.status}</span>
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">{new Date(j.startedAt).toLocaleString()}</td>
                    <td className="px-4 py-2 text-right">{j.importedItems} / {j.failedItems} / {j.totalItems}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <h2 className="mb-3 font-display text-lg font-semibold">Recent import errors</h2>
        {errors.isSuccess && errors.data.data.length === 0 && <p className="text-sm text-muted-foreground">No errors logged.</p>}
        {errors.isSuccess && errors.data.data.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-surface text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr><th className="px-4 py-3">When</th><th className="px-4 py-3">AppID</th><th className="px-4 py-3">Error</th></tr>
              </thead>
              <tbody>
                {errors.data.data.slice(0, 50).map((e: any) => (
                  <tr key={e.id} className="border-t border-border/60">
                    <td className="px-4 py-2 text-muted-foreground">{new Date(e.createdAt).toLocaleString()}</td>
                    <td className="px-4 py-2">{e.steamAppid ?? "—"}</td>
                    <td className="px-4 py-2 text-destructive">{e.errorMessage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function RunningJobBanner({ job }: { job: ImportJob }) {
  const processed = job.importedItems + job.failedItems;
  const total = job.totalItems || 0;
  const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
  const elapsedMs = Date.now() - new Date(job.startedAt).getTime();
  const rate = processed > 0 && elapsedMs > 0 ? processed / (elapsedMs / 1000) : 0; // items/sec
  const remaining = total - processed;
  const etaSec = rate > 0 && remaining > 0 ? Math.round(remaining / rate) : null;
  const fmt = (s: number) => {
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
    return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
  };
  return (
    <div className="rounded-xl border border-primary/40 bg-primary/5 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-sm font-semibold">
          Running: <span className="text-primary">{job.source}</span>
        </div>
        <div className="text-xs text-muted-foreground">
          {processed.toLocaleString()} / {total.toLocaleString()} processed
          {" · "}updated {job.importedItems.toLocaleString()}
          {" · "}failed {job.failedItems.toLocaleString()}
          {" · "}elapsed {fmt(Math.floor(elapsedMs / 1000))}
          {etaSec != null && ` · ETA ${fmt(etaSec)}`}
        </div>
      </div>
      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-surface">
        <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Card({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <h3 className="font-display text-sm font-semibold">{title}</h3>
      <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      <div className="mt-3">{children}</div>
    </div>
  );
}
